import {
  DataType,
  Form,
  FormDialogMode,
  FormOptions,
  Section,
  SelectOption
} from '@globaleris/ui.library'
import { Box, makeStyles, useTheme } from '@material-ui/core'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskTypeModel } from '../../../../api/Argentia/endpoints/tasks/model/tasks'
import { EnumCriticidad } from '../../../../api/oData/enums/enumCriticidad'
import { EnumDominioTarea } from '../../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoTarea } from '../../../../api/oData/enums/enumEstadoTarea'
import { enumToSelectOptionArray } from '../../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../../helpers/validateFormHelper'
import { getActiveUser, getCustomerDomain } from '../../../../redux'
import { mapTaskModelToTaskEditForm } from '../../tasks.mapper'
import { TaskAddEditFormModel, TaskTypeSelectOption } from '../../tasks.model'
import { styles } from './taskAddEditForm.styles'

export interface TaskAddEditFormValidation {
  validateForm: () => boolean
}

export interface TaskAddEditFormProps {
  formMode?: FormDialogMode // handle differences between add and edit form
  taskData?: TaskAddEditFormModel // initial data to be displayed in the form
  maxChar?: number // max characters for description field
  isFetchingUsers?: boolean // loading spinner for users
  isFetchingTaskDetails?: boolean // loading spinner for relatedEntityCode
  taskDetails?: TaskAddEditFormModel // taskDetails holds relatedEntityCode info
  globClientUsersOptions?: SelectOption[] // users selectOption from globalerisClient
  atlasUsersOptions?: SelectOption[]
  relatedEntityCodeOptions?: SelectOption[] // relatedEntityCode selectOption - must be equal to taskDetails.relatedEntityCode
  taskTypes?: TaskTypeSelectOption[] // taskTypes selectOption
  isFetchingTaskTypes?: boolean // loading spinner for taskTypes
  isFetchingAtlasUsers?: boolean
  hasAtlasAdminRole?: boolean
  onFormDataChange: (updatedFormData: TaskAddEditFormModel) => void // callback to update parent state
  assignedActiveUser?: boolean // triggers useEffect to assign activeUser to assignedUser field
}

const DEFAULT_MAX_CHAR = 1000

export const TaskAddEditForm = forwardRef<TaskAddEditFormValidation, TaskAddEditFormProps>(
  (
    {
      formMode,
      taskData,
      maxChar = DEFAULT_MAX_CHAR,
      isFetchingUsers,
      isFetchingTaskDetails,
      taskDetails,
      globClientUsersOptions,
      atlasUsersOptions,
      relatedEntityCodeOptions,
      taskTypes,
      isFetchingTaskTypes,
      isFetchingAtlasUsers,
      hasAtlasAdminRole = false,
      onFormDataChange,
      assignedActiveUser = false
    },
    ref
  ) => {
    const [t] = useTranslation(['globalerisLibrary', 'tasks', 'enums'])
    const currentTheme = useTheme()
    const useStyles = makeStyles(styles(currentTheme))
    const classes = useStyles()

    const [formData, setFormData] = useState<TaskAddEditFormModel>(taskData as TaskAddEditFormModel) // related>EntityCode would be added after fetching the taskId
    const [closingDateIsRequired, setClosingDateIsRequired] = useState<boolean>(false)
    const initialDescriptionLength = taskData?.description?.length ?? 0
    const [remainingChars, setRemainingChars] = useState<number>(maxChar - initialDescriptionLength)
    const activeUser = getActiveUser()

    // #region Handlers Starts
    // #endregion Handlers Ends

    // #region Functions Starts

    // #endregion Functions Ends

    // #region Form Definition Starts
    const formOptions: FormOptions = {
      elevation: 1,
      columns: 4,
      analytics: {
        customer: getCustomerDomain(),
        view: t('tasks:editTask.form.analytics')
      }
    }

    const formSections: Section[] = [
      {
        // title: t('tasks:forms.add.section1Title'),
        inputs: [
          {
            name: 'id',
            type: DataType.NUMBER,
            display: false,
            readOnly: true
          },
          {
            // selecting a taskType will fill taskType.id Post request
            name: 'taskType.id',
            label: t('tasks:editTask.form.inputs.taskTypeId'),
            type: DataType.SELECT,
            selectOptions: taskTypes as SelectOption[],
            isLoading: isFetchingTaskTypes,
            required: true,
            inputSize: 4,
            display: formMode === FormDialogMode.ADD,
            onChange: (value, formState) => {
              const formData: TaskAddEditFormModel = formState as TaskAddEditFormModel
              const selectedTaskType = taskTypes?.find(t => t.value === value)
              const updatedFormData: TaskAddEditFormModel = {
                ...formData,
                taskType: {
                  id: +value,
                  code: selectedTaskType?.code ?? '',
                  description: selectedTaskType?.description ?? ''
                } as TaskTypeModel,
                criticality: selectedTaskType?.criticality ?? EnumCriticidad.Leve
              }
              onFormDataChange(updatedFormData)
            }
          },
          {
            // it is called taskType
            name: 'taskType.description',
            label: t('tasks:editTask.form.inputs.taskType'),
            type: DataType.TEXT,
            readOnly: true,
            display: formMode === FormDialogMode.EDIT,
            inputSize: 3
          },
          {
            name: 'assignedUser',
            label: t('tasks:editTask.form.inputs.assignedUser'),
            type: DataType.SELECT,
            selectOptions: globClientUsersOptions,
            isLoading: isFetchingUsers,
            inputSize: formMode === FormDialogMode.EDIT ? 3 : 4,
            onChange: (value, formState) => {
              onFormDataChange(formState as TaskAddEditFormModel)
            }
          },
          // {
          //   name: 'taskType.description',
          //   label: t('tasks:editTask.form.inputs.taskTypeDescription'),
          //   type: DataType.TEXT,
          //   readOnly: true,
          //   inputSize: formMode === FormDialogMode.EDIT ? 3 : 4
          // },
          {
            name: 'criticality',
            label: t('tasks:editTask.form.inputs.criticality'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumCriticidad, 'enums:EnumCriticidad'),
            required: true,
            inputSize: formMode === FormDialogMode.EDIT ? 3 : 4,
            onChange: (value, formState) => {
              onFormDataChange(formState as TaskAddEditFormModel)
            }
          },
          {
            name: 'status',
            label: t('tasks:editTask.form.inputs.status'),
            type: DataType.SELECT,
            defaultValue: formMode === FormDialogMode.ADD ? EnumEstadoTarea.Nueva : null,
            readOnly: formMode === FormDialogMode.ADD,
            selectOptions: enumToSelectOptionArray(EnumEstadoTarea, 'enums:EnumEstadoTarea'),
            required: true,
            inputSize: formMode === FormDialogMode.EDIT ? 3 : 4,
            onChange: (value, formState) => {
              onFormDataChange(formState as TaskAddEditFormModel)
              if (+value === EnumEstadoTarea.Finalizada) {
                setClosingDateIsRequired(true)
              } else {
                setClosingDateIsRequired(false)
              }
            }
          },
          {
            name: 'creationDate',
            label: t('tasks:editTask.form.inputs.creationDate'),
            type: DataType.DATE,
            inputSize: formMode === FormDialogMode.EDIT ? 3 : 4,
            display: formMode === FormDialogMode.EDIT,
            onChange: (value, formState) => {
              onFormDataChange(formState as TaskAddEditFormModel)
            }
          },
          {
            name: 'closingDate',
            label: t('tasks:editTask.form.inputs.closingDate'),
            type: DataType.DATE,
            inputSize: formMode === FormDialogMode.EDIT ? 3 : 4,
            display: formMode === FormDialogMode.EDIT,
            required: closingDateIsRequired,
            onChange: (value, formState) => {
              onFormDataChange(formState as TaskAddEditFormModel)
            }
          },
          {
            name: 'taskType.domain',
            label: t('tasks:editTask.form.inputs.domain'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumDominioTarea, 'enums:EnumDominioTarea'),
            readOnly: true,
            display: formMode === FormDialogMode.EDIT,
            inputSize: 3
          },
          {
            name: 'relatedEntityCode',
            label: t('tasks:editTask.form.inputs.relatedEntityCode'),
            type: DataType.SELECT,
            selectOptions: relatedEntityCodeOptions ?? [{} as SelectOption], // selectOption is introduced to handle isLoading spinner
            readOnly: true,
            display: formMode === FormDialogMode.EDIT,
            isLoading: isFetchingTaskDetails,
            inputSize: 3
          }
        ]
      },
      {
        // title: t('tasks:forms.add.section1Title'),
        inputs: [
          {
            name: 'description',
            label: t('tasks:editTask.form.inputs.description'),
            type: DataType.LONGTEXT,
            inputSize: 12,
            maxLength: maxChar,
            helpText: `${remainingChars}/${maxChar}`,
            onChange: (value, formState) => {
              setRemainingChars(maxChar - value.toString().length)
              onFormDataChange(formState as TaskAddEditFormModel)
            }
          }
        ]
      },
      ...(hasAtlasAdminRole
        ? [
            {
              title: t('tasks:editTask.form.sections.atlasUser'),
              collapsible: true,
              inputs: [
                {
                  name: 'authorNameAtlas',
                  label: t('tasks:editTask.form.inputs.authorNameAtlas'),
                  type: DataType.SELECT,
                  selectOptions: atlasUsersOptions,
                  isLoading: isFetchingAtlasUsers,
                  clearable: true,
                  inputSize: 6 as 6,
                  onChange: (value, formState) => {
                    onFormDataChange({
                      ...(formState as TaskAddEditFormModel),
                      authorNameAtlas: value || null
                    })
                  }
                }
              ]
            }
          ]
        : [])
    ]

    // #endregion Form Definition Ends

    // #region DataTable Config Starts
    // #endregion DataTable Config Ends

    // #region Hooks Starts
    useEffect(() => {
      if (assignedActiveUser) {
        const updatedFormData = { ...formData, assignedUser: activeUser.name }
        setFormData(updatedFormData)
        onFormDataChange(updatedFormData)
      }
    }, [assignedActiveUser])

    useEffect(() => {
      if (taskDetails) {
        const updatedFormData = {
          ...formData,
          relatedEntityCode: mapTaskModelToTaskEditForm(taskDetails).relatedEntityCode,
          authorNameAtlas: taskDetails.authorNameAtlas
        }
        setFormData(updatedFormData)
        onFormDataChange(updatedFormData)
      }
    }, [taskDetails])

    // #endregion Hooks Ends

    // #region useRef functions Starts
    const validateForm = () => {
      return missingFieldsFormWithRequiredText(formSections, formData)
    }

    useImperativeHandle(ref, () => ({
      validateForm
    }))
    // #endregion useRef functions Ends

    return (
      <Box className={classes.formStyleWrapper}>
        {/* This wrapper is added to modify internal FormInput globalerisUI library component css*/}
        <Form
          title={t('tasks:editTask.form.title')}
          sections={formSections}
          data={formData}
          options={formOptions}
        />
      </Box>
    )
  }
)
